import { useState } from 'react';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import * as css from './CommentInput.css';

type CommentInputProps = {
  placeholder?: string;
  onSubmit: (text: string) => Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  maxLength?: number;
};

export const CommentInput = ({
  placeholder = 'Write a comment...',
  onSubmit,
  isLoading = false,
  disabled = false,
  maxLength = 500,
}: CommentInputProps) => {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim() || isSubmitting || disabled) return;

    setIsSubmitting(true);
    await onSubmit(text.trim());
    setText('');
    setIsSubmitting(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isButtonDisabled =
    !text.trim() || isSubmitting || isLoading || disabled;
  const remainingChars = maxLength - text.length;
  const isNearLimit = remainingChars <= 50;
  const isOverLimit = remainingChars < 0;

  return (
    <div className={css.commentInputContainer}>
      <div className={css.inputWrapper}>
        <textarea
          className={css.textArea}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled || isLoading}
          maxLength={maxLength}
          rows={3}
        />
        <Button
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          size="medium"
          variant="filled"
          className={css.sendButton}
        >
          {isSubmitting ? (
            <div className={css.loadingSpinner} />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} />
          )}
        </Button>
      </div>

      {(isNearLimit || isOverLimit) && (
        <div className={css.characterCount}>
          <Typography
            variant="body"
            size="small"
            color={isOverLimit ? 'error' : 'N-60'}
            fontWeight="normal"
          >
            {remainingChars} characters remaining
          </Typography>
        </div>
      )}
    </div>
  );
};
